import React from 'react';

import type { CodeBlockSuggestion } from '@aline/common';
import { createDocumentRoute, isUUIDV4 } from '@aline/common';
import { CircularProgress, darken, Typography } from '@mui/material';
import clsx from 'clsx';
import { CirclePlusIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AnimatedArrowIcon } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useMaybeDocumentParams } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';

import { $chatThreadState } from '../../../../chat-state';
import { createAIChatItem } from '../../../helpers/aiChatItem';

const useStyles = makeStyles()(theme => ({
  actionButton: {
    '&:hover': {
      backgroundColor: '#F0F0F0',
    },
    alignItems: 'center',
    borderRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1.25),
  },
  actionButtonIcon: {
    height: 18,
    width: 18,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    // Pins header actions to the right...
    marginLeft: 'auto',
  },
  loadingContainer: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  pinnedExpandButton: {
    '&:hover': {
      backgroundColor: darken(theme.appColors.darkBackground, 0.75),
    },
    backgroundColor: theme.appColors.darkBackground,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(0.25),
  },
  suggestionListBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25),
  },
  suggestionListContainer: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, -1),
    position: 'relative',
  },
  suggestionListHeader: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  title: {
    marginLeft: theme.spacing(0.5),
  },
}));

export const PinnedExpandCollapseButton = React.memo(function PinnedExpandCollapseButton({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.pinnedExpandButton} onClick={() => setIsOpen(!isOpen)}>
      <AnimatedArrowIcon rotated={isOpen} />
    </div>
  );
});

export const SuggestionList = React.memo(function SuggestionList({
  children,
  firstLoadedSuggestion,
  isLoading,
  messageId,
  threadId,
}: {
  children: React.ReactNode;
  firstLoadedSuggestion: CodeBlockSuggestion | null;
  isLoading: boolean;
  messageId: string;
  threadId: string;
}) {
  const { classes } = useStyles();
  /**
   * The suggestion's documentId comes straight off the parsed model stream, so
   * it can be empty or an invented placeholder when no document was attached.
   * `GetSuggestionDocument` types it as `UUID!`, which rejects a non-UUID at
   * variable coercion and surfaces a raw GraphQL error on the page, so only
   * query once the id is a real UUID.
   */
  const suggestionDocumentId = firstLoadedSuggestion?.documentId;
  const queryableDocumentId =
    suggestionDocumentId && isUUIDV4(suggestionDocumentId) ? suggestionDocumentId : null;

  const { data } = Apollo.useGetSuggestionDocumentQuery({
    skip: !queryableDocumentId,
    variables: { documentId: queryableDocumentId ?? '' },
  });
  const { documentId: urlDocumentId } = useMaybeDocumentParams();
  /**
   * `urlDocumentId` is undefined outside a document route, and so is the
   * suggestion's id when the model never anchored one. Require a real id on
   * both sides, otherwise two absent values compare equal and every card
   * claims to be the current document.
   */
  const isCurrentDocument = Boolean(urlDocumentId) && suggestionDocumentId === urlDocumentId;
  const displayDocumentName = isCurrentDocument
    ? 'Current Document'
    : data?.document?.name || 'Untitled Document';

  return (
    <section className={classes.suggestionListContainer}>
      <div className={classes.suggestionListHeader}>
        {isLoading && (
          <div className={classes.loadingContainer}>
            <CircularProgress size={18} />
          </div>
        )}
        <Typography noWrap className={classes.title} variant="subtitle1">
          {isLoading ? 'Generating edits...' : displayDocumentName}
        </Typography>
        <div className={classes.headerActions}>
          <SuggestionListActionButton
            firstLoadedSuggestion={firstLoadedSuggestion}
            messageId={messageId}
            threadId={threadId}
          />
        </div>
      </div>
      <div className={classes.suggestionListBody}>{children}</div>
    </section>
  );
});

function SuggestionListActionButton({
  firstLoadedSuggestion,
  messageId,
  threadId,
}: {
  firstLoadedSuggestion: CodeBlockSuggestion | null;
  messageId: string;
  threadId: string;
}) {
  const { documentId: urlDocumentId } = useMaybeDocumentParams();
  const docNotInView = !urlDocumentId || urlDocumentId !== firstLoadedSuggestion?.documentId;

  if (docNotInView && firstLoadedSuggestion)
    return (
      <OpenSuggestionListDocumentButton
        firstLoadedSuggestion={firstLoadedSuggestion}
        messageId={messageId}
        threadId={threadId}
      />
    );

  return null;
}

function OpenSuggestionListDocumentButton({
  firstLoadedSuggestion,
  messageId,
  threadId,
}: {
  firstLoadedSuggestion: CodeBlockSuggestion;
  messageId: string;
  threadId: string;
}) {
  const { classes } = useStyles();
  const navigate = useNavigate();

  return (
    <div
      className={clsx(classes.actionButton)}
      onClick={() => {
        const documentRoute = createDocumentRoute(firstLoadedSuggestion.documentId, {
          aiChatItem: createAIChatItem({
            groupId: firstLoadedSuggestion.groupId,
            messageId,
            threadId,
            type: 'suggestion',
          }),
        });
        navigate(documentRoute);
      }}
    >
      <CirclePlusIcon className={classes.actionButtonIcon} strokeWidth={1.5} />
      <Typography className={classes.actionButtonText}>View suggestions in doc</Typography>
    </div>
  );
}

export function useIsAutoScrollChatItemInSuggestions({
  messageId,
  suggestionIds,
  threadId,
}: {
  messageId: string;
  suggestionIds: UUID[];
  threadId: string;
}) {
  const chatThreadState = useSnapshot($chatThreadState);
  const { autoScrollAIChatItem } = chatThreadState;

  return {
    chatThreadState,
    isAutoScrollChatItemInSuggestions:
      autoScrollAIChatItem?.threadId === threadId &&
      autoScrollAIChatItem?.type === 'suggestion' &&
      autoScrollAIChatItem?.messageId === messageId &&
      suggestionIds.some(suggestionId => autoScrollAIChatItem.groupId === suggestionId),
  };
}
